import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Chip,
    Divider,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Close, FilterList, Clear } from '@mui/icons-material';
import { IVideoManagementFilters } from './types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface IFilterModalProps {
    open: boolean;
    onClose: () => void;
    filters: IVideoManagementFilters;
    onApplyFilters: (filters: Partial<IVideoManagementFilters>) => void;
}

const categoryOptions = [
    { value: 'picture', label: 'Picture' },
    { value: 'skit', label: 'Skit' },
    { value: 'poster', label: 'Poster' },
    { value: 'video', label: 'Video' },
    { value: 'audio', label: 'Audio' },
];

const sortOptions = [
    { value: 'total_average', label: 'Score' },
    { value: 'total_votes', label: 'Votes' },
    { value: 'createdAt', label: 'Date Created' },
];

const orderOptions = [
    { value: 'desc', label: 'Highest First' },
    { value: 'asc', label: 'Lowest First' },
];

const selectionStateOptions = [
    { value: '', label: 'All States' },
    { value: 'selected', label: 'Selected' },
    { value: 'not_selected', label: 'Not Selected' },
    { value: 'pending', label: 'Pending' },
];

export const FilterModal: React.FC<IFilterModalProps> = ({ open, onClose, filters, onApplyFilters }) => {
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const { themeStyle } = useThemeContext();

    // Ensure filters have proper default values to prevent undefined errors
    const defaultFilters: IVideoManagementFilters = useMemo(() => {
        return {
            page: 1,
            limit: 9,
            category: [],
            searchTerm: '',
            selection_state: '',
            selection_email_sent: '',
            pre_selection_completed: '',
        };
    }, []);

    const [localFilters, setLocalFilters] = useState<IVideoManagementFilters>({
        ...defaultFilters,
        ...filters,
    });

    useEffect(() => {
        // Merge incoming filters with defaults to ensure all required fields exist
        setLocalFilters({
            ...defaultFilters,
            ...filters,
        });
    }, [filters, defaultFilters]);

    const handleFilterChange = (key: keyof IVideoManagementFilters, value: any) => {
        setLocalFilters(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleCategoryToggle = (category: string) => {
        const currentCategories = localFilters.category || [];
        const newCategories = currentCategories.includes(category)
            ? currentCategories.filter(c => c !== category)
            : [...currentCategories, category];

        handleFilterChange('category', newCategories);
    };

    const handleClearFilters = () => {
        const clearedFilters: Partial<IVideoManagementFilters> = {
            category: [],
            selection_state: '',
            selection_email_sent: '',
            pre_selection_completed: '',
            sort: 'total_average',
            order: 'desc',
        };
        setLocalFilters(prev => ({ ...prev, ...clearedFilters }));
    };

    const handleApply = () => {
        onApplyFilters(localFilters);
        onClose();
    };

    const hasActiveFilters =
        (localFilters.category && localFilters.category.length > 0) ||
        localFilters.selection_state !== '' ||
        localFilters.selection_email_sent !== '' ||
        localFilters.pre_selection_completed !== '' ||
        (localFilters.sort && localFilters.order);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    maxHeight: isMobile ? '100%' : '80vh',
                    backgroundColor: theme[themeStyle].deep_grey,
                    color: theme[themeStyle].tag_white,
                },
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                    backgroundColor: theme[themeStyle].deep_grey,
                    color: theme[themeStyle].tag_white,
                    borderBottom: `1px solid ${theme[themeStyle].tag_white}20`,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterList sx={{ color: theme[themeStyle].tag_white }} />
                    <Typography variant="h6" sx={{ color: theme[themeStyle].tag_white }}>
                        Filters
                    </Typography>
                </Box>
                <Button
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: theme[themeStyle].tag_white,
                        '&:hover': {
                            backgroundColor: `${theme[themeStyle].tag_white}20`,
                        },
                    }}>
                    <Close />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pt: 0, backgroundColor: theme[themeStyle].deep_grey }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Category Filter */}
                    <Box>
                        <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            gutterBottom
                            sx={{ color: theme[themeStyle].tag_white }}>
                            Project Type
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {categoryOptions.map(option => (
                                <Chip
                                    key={option.value}
                                    label={option.label}
                                    variant={localFilters.category?.includes(option.value) ? 'filled' : 'outlined'}
                                    color={localFilters.category?.includes(option.value) ? 'primary' : 'default'}
                                    onClick={() => handleCategoryToggle(option.value)}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: localFilters.category?.includes(option.value)
                                            ? theme[themeStyle].tag_white
                                            : 'transparent',
                                        color: localFilters.category?.includes(option.value)
                                            ? theme[themeStyle].deep_grey
                                            : theme[themeStyle].tag_white,
                                        borderColor: theme[themeStyle].tag_white,
                                        '&:hover': {
                                            backgroundColor: localFilters.category?.includes(option.value)
                                                ? `${theme[themeStyle].tag_white}90`
                                                : `${theme[themeStyle].tag_white}20`,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Divider sx={{ borderColor: `${theme[themeStyle].tag_white}30` }} />

                    {/* Selection State Filter */}
                    <Box>
                        <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            gutterBottom
                            sx={{ color: theme[themeStyle].tag_white }}>
                            Selection State
                        </Typography>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Selection State</InputLabel>
                            <Select
                                value={localFilters.selection_state || ''}
                                onChange={e => handleFilterChange('selection_state', e.target.value)}
                                displayEmpty
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray,
                                            borderWidth: '1px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                            borderWidth: '1px',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                            borderWidth: '2px',
                                        },
                                    },
                                    '& .MuiSelect-select': {
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: theme[themeStyle].dropdown_black,
                                            border: `1px solid ${theme[themeStyle].form_divider}`,
                                            borderRadius: '8px',
                                            '& .MuiMenuItem-root': {
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].form_divider,
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].highlight,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}>
                                {selectionStateOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Email Status Filters */}
                    <Box>
                        <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            gutterBottom
                            sx={{ color: theme[themeStyle].tag_white }}>
                            Email Status
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={localFilters.selection_email_sent === true}
                                        onChange={e =>
                                            handleFilterChange('selection_email_sent', e.target.checked ? true : '')
                                        }
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: theme[themeStyle].tag_white,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ color: theme[themeStyle].tag_white }}>
                                        Selection Email Sent
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={localFilters.pre_selection_completed === true}
                                        onChange={e =>
                                            handleFilterChange('pre_selection_completed', e.target.checked ? true : '')
                                        }
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: theme[themeStyle].tag_white,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ color: theme[themeStyle].tag_white }}>
                                        Pre-selection Completed
                                    </Typography>
                                }
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ borderColor: `${theme[themeStyle].tag_white}30` }} />

                    {/* Sort Options */}
                    <Box>
                        <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            gutterBottom
                            sx={{ color: theme[themeStyle].tag_white }}>
                            Sort By
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Sort Field</InputLabel>
                                <Select
                                    value={localFilters.sort || 'total_average'}
                                    onChange={e => handleFilterChange('sort', e.target.value)}
                                    label="Sort Field"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray,
                                                borderWidth: '1px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                                borderWidth: '1px',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                                borderWidth: '2px',
                                            },
                                        },
                                        '& .MuiSelect-select': {
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].dropdown_black,
                                                border: `1px solid ${theme[themeStyle].form_divider}`,
                                                borderRadius: '8px',
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].form_divider,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    {sortOptions.map(option => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                            sx={{
                                                color: theme[themeStyle].deep_grey,
                                                '&:hover': {
                                                    backgroundColor: `${theme[themeStyle].tag_white}20`,
                                                },
                                            }}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Order</InputLabel>
                                <Select
                                    value={localFilters.order || 'desc'}
                                    onChange={e => handleFilterChange('order', e.target.value)}
                                    label="Order"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].placeholderGray,
                                                borderWidth: '1px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                                borderWidth: '1px',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                                borderWidth: '2px',
                                            },
                                        },
                                        '& .MuiSelect-select': {
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].dropdown_black,
                                                border: `1px solid ${theme[themeStyle].form_divider}`,
                                                borderRadius: '8px',
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].form_divider,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    {orderOptions.map(option => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                            sx={{
                                                color: theme[themeStyle].deep_grey,
                                                '&:hover': {
                                                    backgroundColor: `${theme[themeStyle].tag_white}20`,
                                                },
                                            }}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* Active Filters Summary */}
                    {hasActiveFilters && (
                        <Box>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                                sx={{ color: `${theme[themeStyle].tag_white}80` }}>
                                Active Filters:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {localFilters.category?.map(cat => (
                                    <Chip
                                        key={cat}
                                        label={cat}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{
                                            backgroundColor: theme[themeStyle].tag_white,
                                            color: theme[themeStyle].deep_grey,
                                            borderColor: theme[themeStyle].tag_white,
                                        }}
                                    />
                                ))}
                                {localFilters.selection_state && (
                                    <Chip
                                        label={
                                            selectionStateOptions.find(
                                                opt => opt.value === localFilters.selection_state,
                                            )?.label
                                        }
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        sx={{
                                            backgroundColor: theme[themeStyle].tag_white,
                                            color: theme[themeStyle].deep_grey,
                                            borderColor: theme[themeStyle].tag_white,
                                        }}
                                    />
                                )}
                                {localFilters.selection_email_sent === true && (
                                    <Chip
                                        label="Email Sent"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{
                                            backgroundColor: theme[themeStyle].tag_white,
                                            color: theme[themeStyle].deep_grey,
                                            borderColor: theme[themeStyle].tag_white,
                                        }}
                                    />
                                )}
                                {localFilters.pre_selection_completed === true && (
                                    <Chip
                                        label="Pre-selection Done"
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        sx={{
                                            backgroundColor: theme[themeStyle].tag_white,
                                            color: theme[themeStyle].deep_grey,
                                            borderColor: theme[themeStyle].tag_white,
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    gap: 2,
                    backgroundColor: theme[themeStyle].deep_grey,
                    borderTop: `1px solid ${theme[themeStyle].tag_white}20`,
                }}>
                <Button
                    onClick={handleClearFilters}
                    startIcon={<Clear />}
                    variant="outlined"
                    color="secondary"
                    sx={{
                        color: theme[themeStyle].tag_white,
                        borderColor: theme[themeStyle].tag_white,
                        '&:hover': {
                            borderColor: theme[themeStyle].tag_white,
                            backgroundColor: `${theme[themeStyle].tag_white}20`,
                        },
                    }}>
                    Clear All
                </Button>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        color: theme[themeStyle].tag_white,
                        borderColor: theme[themeStyle].tag_white,
                        '&:hover': {
                            borderColor: theme[themeStyle].tag_white,
                            backgroundColor: `${theme[themeStyle].tag_white}20`,
                        },
                    }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleApply}
                    variant="contained"
                    disabled={!hasActiveFilters}
                    sx={{
                        backgroundColor: theme[themeStyle].tag_white,
                        color: theme[themeStyle].deep_grey,
                        '&:hover': {
                            backgroundColor: `${theme[themeStyle].tag_white}90`,
                        },
                        '&:disabled': {
                            backgroundColor: `${theme[themeStyle].tag_white}30`,
                            color: `${theme[themeStyle].deep_grey}50`,
                        },
                    }}>
                    Apply Filters
                </Button>
            </DialogActions>
        </Dialog>
    );
};
